import ImportLayout from '@app/components/imports/import-layout';
import GoodsImportTypePage from '@app/components/imports/goods-import/pages/goods-import-type-page';
import GoodsImportIntroPage from '@app/components/imports/goods-import/pages/goods-import-intro-page';
import GoodsImportConfigurePage from '@app/components/imports/goods-import/pages/goods-import-configure-page';
import { CustomRoute } from '@app/components/layout/sidebar/components/route';
import CreateReportIntroPage from '@app/components/reports/create-report/create-report-intro-page';
import CreateReportLayout from '@app/components/reports/create-report/create-report-layout';
import CreateReportMetricPage from '@app/components/reports/create-report/create-report-metric-page';
import CreateReportPublishPage from '@app/components/reports/create-report/create-report-publish';
import CreateReportSetupPage from '@app/components/reports/create-report/create-report-setup-page';
import { FEATURES } from '@app/utils/constants';
import { OperationReportTypeEnum } from '@shared/api/types';
import { Outlet, Route } from 'react-router-dom';
import ExcludedSoldItemsCategoryPage from './excluded-items/pages/excluded-sold-items-category-page';
import ExcludedSoldItemsLandingPage from './excluded-items/pages/excluded-sold-items-landing-page';
import SoldFoodAndDrinkDataCoveragePage from './food-and-drink/pages/sold-food-and-drink-data-coverage-page';
import SoldFoodAndDrinkItemPage from './food-and-drink/pages/sold-food-and-drink-item-page';
import SoldFoodAndDrinkItemSalesPage from './food-and-drink/pages/sold-food-and-drink-item-sales-page';
import SoldFoodAndDrinkListPage from './food-and-drink/pages/sold-food-and-drink-list-page';
import SoldFoodAndDrinkReviewPage from './food-and-drink/pages/sold-food-and-drink-review-page';
import SoldFoodAndDrinkItemContext from './food-and-drink/sold-food-and-drink-item-context';
import SoldFoodAndDrinkItemLayout from './food-and-drink/sold-food-and-drink-item-layout';
import SoldFoodAndDrinkLayout from './food-and-drink/sold-food-and-drink-layout';
import SoldPackagingDataCoveragePage from './packaging/pages/sold-packaging-coverage-page';
import SoldPackagingItemPage from './packaging/pages/sold-packaging-item-page';
import SoldPackagingItemSalesPage from './packaging/pages/sold-packaging-item-sales-page';
import SoldPackagingListPage from './packaging/pages/sold-packaging-list-page';
import SoldPackagingItemContext from './packaging/sold-packaging-item-context';
import SoldPackagingItemLayout from './packaging/sold-packaging-item-layout';
import SoldPackagingLayout from './packaging/sold-packaging-layout';
import SalesReportsListPage from './report/pages/sales-reports-list-page';
import SoldGoodsImportsListPage from './report/pages/sold-goods-imports-list-page';
import SalesReport from './report/sales-report';
import SalesReportDownload from './report/sales-report-download';
import SalesReportContextProvider from './report/sales-report-result-context';

export const soldGoodsRoutes = {
  // general
  SOLD_GOODS: '/sold-goods',
  SOLD_GOODS_IMPORTS_LIST: '/sold-goods/imports',

  // unified import flow (landing → activity types → organise + upload)
  SOLD_GOODS_IMPORT: '/sold-goods/import',
  SOLD_GOODS_IMPORT_TYPES: '/sold-goods/import/types',
  SOLD_GOODS_IMPORT_ORGANISE: '/sold-goods/import/organise',

  // excluded items (Uncertain + Other goods)
  SOLD_EXCLUDED_ITEMS: '/sold-goods/excluded-items',
  SOLD_EXCLUDED_ITEMS_UNCERTAIN: '/sold-goods/excluded-items/review',
  SOLD_EXCLUDED_ITEMS_OTHER: '/sold-goods/excluded-items/other',

  // reports
  SALES_REPORTS: '/sold-goods/reports',
  SALES_REPORT: '/sold-goods/reports/:report_uuid',

  // new report
  SALES_REPORT_CREATE: '/sales-reports/new',
  SALES_REPORT_CREATE_SETUP: '/sales-reports/new/setup',
  SALES_REPORT_CREATE_METRIC: '/sales-reports/new/metric',
  SALES_REPORT_EDIT_SETUP: '/sold-goods/reports/:report_uuid/edit/setup',
  SALES_REPORT_EDIT_METRIC: '/sold-goods/reports/:report_uuid/edit/metric',
  SALES_REPORT_PUBLISH: '/sold-goods/reports/:report_uuid/publish',

  SALES_REPORT_DOWNLOAD: '/sold-goods/reports/:report_uuid/download',

  // food and drink
  SOLD_FOOD_AND_DRINK: '/sold-goods/food-and-drink',
  SOLD_FOOD_AND_DRINK_DATA_COVERAGE: '/sold-goods/food-and-drink/data-coverage',
  SOLD_FOOD_AND_DRINK_REVIEW: '/sold-goods/food-and-drink/review',
  SOLD_FOOD_AND_DRINK_INGREDIENTS: '/sold-goods/food-and-drink/ingredients',
  SOLD_FOOD_AND_DRINK_ITEM: '/sold-goods/food-and-drink/:item_uuid',
  SOLD_FOOD_AND_DRINK_ITEM_SALES: '/sold-goods/food-and-drink/:item_uuid/sales',

  // packaging
  SOLD_PACKAGING: '/sold-goods/packaging',
  SOLD_PACKAGING_ITEM: '/sold-goods/packaging/:item_uuid',
  SOLD_PACKAGING_ITEM_SALES: '/sold-goods/packaging/:item_uuid/sales',
  SOLD_PACKAGING_DATA_COVERAGE: '/sold-goods/packaging/data-coverage',
};

export function SoldGoodsRoutes() {
  return (
    <Route
      element={
        <CustomRoute feature={[FEATURES.salesReporting]} element={<Outlet />} />
      }
    >
      <Route
        path={soldGoodsRoutes.SOLD_GOODS_IMPORTS_LIST}
        element={<SoldGoodsImportsListPage />}
      />

      {/* Unified import flow. New sold-goods imports route here: a single
          landing → content types → organise → upload wizard sharing one
          ImportLayout React Hook Form context (mirrors purchased goods). */}
      <Route
        element={
          <CustomRoute
            element={
              <ImportLayout
                importType="food"
                onClosePath={soldGoodsRoutes.SOLD_GOODS_IMPORTS_LIST}
                reportType={OperationReportTypeEnum.sales}
              />
            }
            hasSidebar={false}
          />
        }
      >
        <Route
          path={soldGoodsRoutes.SOLD_GOODS_IMPORT}
          element={<GoodsImportIntroPage />}
        />
        <Route
          path={soldGoodsRoutes.SOLD_GOODS_IMPORT_TYPES}
          element={<GoodsImportTypePage />}
        />
        <Route
          path={soldGoodsRoutes.SOLD_GOODS_IMPORT_ORGANISE}
          element={<GoodsImportConfigurePage />}
        />
      </Route>

      <Route path={soldGoodsRoutes.SOLD_EXCLUDED_ITEMS}>
        {/* Landing page, then a standalone subpage per category. */}
        <Route index element={<ExcludedSoldItemsLandingPage />} />
        <Route
          path={soldGoodsRoutes.SOLD_EXCLUDED_ITEMS_UNCERTAIN}
          element={<ExcludedSoldItemsCategoryPage type="uncertain" />}
        />
        <Route
          path={soldGoodsRoutes.SOLD_EXCLUDED_ITEMS_OTHER}
          element={<ExcludedSoldItemsCategoryPage type="other" />}
        />
      </Route>

      <Route
        path={soldGoodsRoutes.SOLD_FOOD_AND_DRINK}
        element={<SoldFoodAndDrinkLayout />}
      >
        <Route
          path={soldGoodsRoutes.SOLD_FOOD_AND_DRINK}
          index
          element={<SoldFoodAndDrinkListPage />}
        />
        <Route
          path={soldGoodsRoutes.SOLD_FOOD_AND_DRINK_DATA_COVERAGE}
          element={<SoldFoodAndDrinkDataCoveragePage />}
        />
        <Route
          path={soldGoodsRoutes.SOLD_FOOD_AND_DRINK_REVIEW}
          element={<SoldFoodAndDrinkReviewPage />}
        />
      </Route>

      <Route
        path={soldGoodsRoutes.SOLD_FOOD_AND_DRINK_ITEM}
        element={<SoldFoodAndDrinkItemContext />}
      >
        <Route path="" element={<SoldFoodAndDrinkItemLayout />}>
          <Route index element={<SoldFoodAndDrinkItemPage />} />
          <Route
            path={soldGoodsRoutes.SOLD_FOOD_AND_DRINK_ITEM_SALES}
            element={<SoldFoodAndDrinkItemSalesPage />}
          />
        </Route>
      </Route>

      <Route
        path={soldGoodsRoutes.SOLD_PACKAGING}
        element={<SoldPackagingLayout />}
      >
        <Route
          path={soldGoodsRoutes.SOLD_PACKAGING}
          index
          element={<SoldPackagingListPage />}
        />
        <Route
          path={soldGoodsRoutes.SOLD_PACKAGING_DATA_COVERAGE}
          element={<SoldPackagingDataCoveragePage />}
        />
      </Route>
      <Route
        path={soldGoodsRoutes.SOLD_PACKAGING_ITEM}
        element={<SoldPackagingItemContext />}
      >
        <Route path="" element={<SoldPackagingItemLayout />}>
          <Route index element={<SoldPackagingItemPage />} />
          <Route
            path={soldGoodsRoutes.SOLD_PACKAGING_ITEM_SALES}
            element={<SoldPackagingItemSalesPage />}
          />
        </Route>
      </Route>

      {/* New report  */}
      <Route
        element={
          <CustomRoute
            hasSidebar={false}
            element={
              <CreateReportLayout
                reportType={OperationReportTypeEnum.sales}
                onClosePath={soldGoodsRoutes.SALES_REPORTS}
              />
            }
          />
        }
      >
        <Route
          path={soldGoodsRoutes.SALES_REPORT_CREATE}
          element={<CreateReportIntroPage />}
        />
        <Route
          path={soldGoodsRoutes.SALES_REPORT_CREATE_SETUP}
          element={<CreateReportSetupPage />}
        />
        <Route
          path={soldGoodsRoutes.SALES_REPORT_CREATE_METRIC}
          element={<CreateReportMetricPage />}
        />
      </Route>

      {/* Edit report */}
      <Route
        element={
          <CustomRoute
            hasSidebar={false}
            element={
              <CreateReportLayout
                reportType={OperationReportTypeEnum.sales}
                onClosePath={soldGoodsRoutes.SALES_REPORTS}
              />
            }
          />
        }
      >
        <Route
          path={soldGoodsRoutes.SALES_REPORT_EDIT_SETUP}
          element={<CreateReportSetupPage />}
        />
        <Route
          path={soldGoodsRoutes.SALES_REPORT_EDIT_METRIC}
          element={<CreateReportMetricPage />}
        />
      </Route>

      <Route
        path={soldGoodsRoutes.SALES_REPORT_PUBLISH}
        element={
          <CustomRoute
            hasSidebar={false}
            element={
              <CreateReportPublishPage
                reportType={OperationReportTypeEnum.sales}
              />
            }
          />
        }
      />

      {/* Reports */}
      <Route
        path={soldGoodsRoutes.SALES_REPORTS}
        element={<SalesReportsListPage />}
      />

      <Route
        path={soldGoodsRoutes.SALES_REPORT}
        element={
          <CustomRoute
            hasSidebar={false}
            element={<SalesReportContextProvider />}
          />
        }
      >
        <Route index element={<SalesReport />} />
        <Route
          path={soldGoodsRoutes.SALES_REPORT_DOWNLOAD}
          element={<SalesReportDownload />}
        />
      </Route>
    </Route>
  );
}
